import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  type Table as TanstackTable,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { COMPLIANCE_STUDIOS } from '@artem-lex/common-server/complianceStudio'
import {
  useDeleteUserStandard,
  type UserStandard,
} from '@/shared/hooks/api/userStandards'
import { BookOpen, Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'
import { type NavigateFn, useTableUrlState } from '@/shared/hooks/use-table-url-state'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DataTablePagination, DataTableToolbar } from '@/components/data-table'
import {
  STANDARDS_LIBRARY_FILTER_VALUES,
  useStandardsLibraryColumns,
} from './standards-library-columns'
import { DeleteStandardDialog } from './DeleteStandardDialog'

type StandardsLibraryTableProps = {
  data: UserStandard[]
  search: Record<string, unknown>
  navigate: NavigateFn
  onRowClick?: (standardId: string) => void
  onAddStandard?: () => void
}

type ToolbarFilter = {
  columnId: string
  title: string
  options: {
    label: string
    value: string
  }[]
}

function ActiveFilterBadges({
  table,
  filters,
  searchLabel,
  removeFilterLabel,
}: {
  table: TanstackTable<UserStandard>
  filters: ToolbarFilter[]
  searchLabel: string
  removeFilterLabel: string
}) {
  const activeBadges: Array<{
    key: string
    label: string
    onRemove: () => void
  }> = []

  const filterByColumnId = new Map(
    filters.map((filter) => [filter.columnId, filter])
  )

  for (const columnFilter of table.getState().columnFilters ?? []) {
    const config = filterByColumnId.get(columnFilter.id)
    if (!config) {
      continue
    }
    const selectedValues = Array.isArray(columnFilter.value)
      ? columnFilter.value
      : [columnFilter.value]

    for (const selected of selectedValues) {
      const selectedValue = String(selected)
      const selectedLabel =
        config.options.find((option) => option.value === selectedValue)
          ?.label ?? selectedValue

      activeBadges.push({
        key: `${columnFilter.id}:${selectedValue}`,
        label: `${config.title}: ${selectedLabel}`,
        onRemove: () => {
          const column = table.getColumn(columnFilter.id)
          if (!column) {
            return
          }

          const currentValue = column.getFilterValue()
          if (Array.isArray(currentValue)) {
            const nextValue = currentValue.filter(
              (value) => String(value) !== selectedValue
            )
            column.setFilterValue(nextValue.length > 0 ? nextValue : undefined)
            return
          }

          column.setFilterValue(undefined)
        },
      })
    }
  }

  const globalFilter = String(table.getState().globalFilter ?? '').trim()
  if (globalFilter) {
    activeBadges.unshift({
      key: `global:${globalFilter}`,
      label: `${searchLabel}: ${globalFilter}`,
      onRemove: () => table.setGlobalFilter(''),
    })
  }

  if (activeBadges.length === 0) {
    return null
  }

  return (
    <div className='flex flex-wrap items-center gap-2'>
      {activeBadges.map((badge) => (
        <Badge key={badge.key} variant='secondary' asChild>
          <button
            type='button'
            onClick={badge.onRemove}
            aria-label={`${removeFilterLabel}: ${badge.label}`}
            className='cursor-pointer'
          >
            {badge.label}
            <X className='size-3' />
          </button>
        </Badge>
      ))}
    </div>
  )
}

function globalFilterFn(
  row: { getValue: (id: string) => unknown; original: UserStandard },
  _columnId: string,
  filterValue: string
): boolean {
  const query = String(filterValue ?? '')
    .trim()
    .toLowerCase()
  if (!query) {
    return true
  }
  const reference = String(row.original.reference ?? '').toLowerCase()
  const title = String(row.original.title ?? '').toLowerCase()
  return reference.includes(query) || title.includes(query)
}

export function StandardsLibraryTable({
  data,
  search,
  navigate,
  onRowClick,
  onAddStandard,
}: StandardsLibraryTableProps) {
  const { t } = useTranslation(['standardsLibrary', 'common'])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    complianceStudioId: false,
    complianceStudios: false,
    createdByUser: false,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'reference', desc: false },
  ])
  const [standardToDelete, setStandardToDelete] = useState<UserStandard | null>(
    null
  )
  const complianceStudios = COMPLIANCE_STUDIOS
  const { mutate: deleteUserStandard, isPending: isDeletingStandard } =
    useDeleteUserStandard()

  const handleRequestDelete = useCallback((standard: UserStandard) => {
    setStandardToDelete(standard)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    if (!standardToDelete) {
      return
    }
    deleteUserStandard(standardToDelete.id, {
      onSuccess: () => {
        toast.success(t('deleteDialog.success'))
        setStandardToDelete(null)
      },
      onError: (error) => {
        toast.error(error.message || t('deleteDialog.error'))
      },
    })
  }, [deleteUserStandard, standardToDelete, t])

  const columns = useStandardsLibraryColumns({
    onDeleteStandard: handleRequestDelete,
    complianceStudios,
  })

  const {
    pagination,
    onPaginationChange,
    ensurePageInRange,
    columnFilters,
    onColumnFiltersChange,
    globalFilter,
    onGlobalFilterChange,
  } = useTableUrlState({
    search,
    navigate,
    pagination: { defaultPage: 1, defaultPageSize: 20 },
    globalFilter: { enabled: true, key: 'q', trim: true },
    columnFilters: [
      { columnId: 'status', searchKey: 'status', type: 'array' },
      { columnId: 'createdByUser', searchKey: 'createdByUser', type: 'array' },
      { columnId: 'dataStatus', searchKey: 'dataStatus', type: 'array' },
      {
        columnId: 'complianceStudioId',
        searchKey: 'complianceStudioId',
        type: 'array',
      },
    ],
  })

  const toolbarFilters = useMemo<ToolbarFilter[]>(
    () => [
      {
        columnId: 'status',
        title: t('columnStatus'),
        options: [
          {
            value: STANDARDS_LIBRARY_FILTER_VALUES.status.current,
            label: t('createNew.statusCurrent'),
          },
          {
            value: STANDARDS_LIBRARY_FILTER_VALUES.status.withdrawn,
            label: t('createNew.statusWithdrawn'),
          },
        ],
      },
      {
        columnId: 'createdByUser',
        title: t('columnCreatedByUser'),
        options: [
          {
            value: STANDARDS_LIBRARY_FILTER_VALUES.boolean.yes,
            label: t('createdByUserYes'),
          },
          {
            value: STANDARDS_LIBRARY_FILTER_VALUES.boolean.no,
            label: t('createdByUserNo'),
          },
        ],
      },
      {
        columnId: 'dataStatus',
        title: t('columnExtractedInfo'),
        options: [
          {
            value: STANDARDS_LIBRARY_FILTER_VALUES.dataStatus.ok,
            label: t('filterDataStatusOk'),
          },
          {
            value: STANDARDS_LIBRARY_FILTER_VALUES.dataStatus.notExtracted,
            label: t('filterDataStatusNotExtracted'),
          },
          {
            value: STANDARDS_LIBRARY_FILTER_VALUES.dataStatus.noPdf,
            label: t('filterDataStatusNoPdf'),
          },
        ],
      },
      {
        columnId: 'complianceStudioId',
        title: t('filterRelevantTo'),
        options: complianceStudios.map((studio) => ({
          value: studio.id,
          label: studio.title,
        })),
      },
    ],
    [complianceStudios, t]
  )

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility,
      globalFilter,
    },
    onPaginationChange,
    onSortingChange: setSorting,
    onColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange,
    globalFilterFn,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  })

  useEffect(() => {
    ensurePageInRange(table.getPageCount())
  }, [table, ensurePageInRange])

  if (data.length === 0) {
    return (
      <div className='flex flex-1 flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16'>
        <BookOpen className='size-10 text-muted-foreground/50' />
        <div className='space-y-1 text-center'>
          <h3 className='text-lg font-semibold'>
            {t('emptyState.title')}
          </h3>
          <p className='text-sm text-muted-foreground'>
            {t('emptyState.description')}
          </p>
        </div>
        {onAddStandard && (
          <Button onClick={onAddStandard}>
            <Plus className='size-4' />
            {t('emptyState.action')}
          </Button>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'max-sm:has-[div[role="toolbar"]]:mb-16',
        'flex flex-1 flex-col gap-4'
      )}
    >
      <DataTableToolbar
        table={table}
        searchPlaceholder={t('searchPlaceholder')}
        filters={toolbarFilters}
        activeFilterBadges={
          <ActiveFilterBadges
            table={table}
            filters={toolbarFilters}
            searchLabel={t('activeFilterSearch')}
            removeFilterLabel={t('removeFilterLabel')}
          />
        }
      />
      <div className='overflow-hidden rounded-md border'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className='group/row'>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className={cn(
                      'bg-background group-hover/row:bg-muted group-data-[state=selected]/row:bg-muted',
                      header.column.columnDef.meta?.className,
                      header.column.columnDef.meta?.thClassName
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={cn('group/row', onRowClick && 'cursor-pointer')}
                  onClick={() => onRowClick?.(row.original.id)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        'bg-background group-hover/row:bg-muted',
                        cell.column.columnDef.meta?.className,
                        cell.column.columnDef.meta?.tdClassName
                      )}
                      onClick={(event) => {
                        if (cell.column.id === 'actions') {
                          event.stopPropagation()
                        }
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center text-muted-foreground'
                >
                  {t('notFound')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} className='mt-auto' />
      <DeleteStandardDialog
        open={Boolean(standardToDelete)}
        onOpenChange={(open) => {
          if (!open) {
            setStandardToDelete(null)
          }
        }}
        standard={standardToDelete}
        isLoading={isDeletingStandard}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
